import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, Table, Text,
} from '@domain-group/suit-theme'

import usePublicationOption from '@hooks/usePublicationOption'
import usePublicationOptionValues from '@hooks/usePublicationOptionValues'
import usePublicationOptions from '@hooks/usePublicationOptions'

import PublicationOptionActions from './PublicationOptionActions'

const buildColumns = (publicationOptions, publicationOptionValues) => [
  {
    title: 'Title',
    cellProps: {
      position: 'sticky',
      left: 0,
    },
    dataKey: 'title',
  },
  {
    title: 'Node',
    dataKey: 'node_name',
  },
  {
    title: 'Placeholder',
    cellProps: {
      key: 'placeholder',
    },
    dataKey: 'id',
    render: (id) => {
      const option = publicationOptions[id] || {}
      const { input_type, placeholder_text } = option

      return (
        <Text variant="ellipsis">
          {input_type === 'select' ? 'N/A' : placeholder_text}
        </Text>
      )
    },
  },
  {
    title: 'Input Type',
    cellProps: {
      key: 'inputType',
    },
    dataKey: 'id',
    render: (id) => {
      const filteredValues = publicationOptionValues.filter(value => value.publication_option_id === id)
      const valuesText = filteredValues.length === 1 ? '(1 Value)' : `(${filteredValues.length} Values)`

      const option = publicationOptions[id] || {}
      const { input_type } = option

      return (
        <Box>
          <Text variant="ellipsis">{input_type}</Text>&nbsp;
          <Text color="bodyFontLightColor" variant="ellipsis">
            {input_type === 'select' ? valuesText : ''}
          </Text>
        </Box>
      )
    },
  },
  {
    title: 'Actions',
    cellProps: {
      textAlign: 'center',
      width: 80,
    },
    dataKey: 'id',
    render: id => <PublicationOptionActions publicationOption={{ id }} />,
  },
]

const PublicationOptions = (props) => {
  const { publicationId } = props

  const publicationOptionsPayload = usePublicationOptions({
    filters: { publication_id: publicationId },
    performHttpRequests: !!publicationId,
  })

  const {
    filteredPublicationOptions,
    hasFilteredPublicationOptions,
    loading,
    publicationOptions,
    publicationOptionsUpdatedAt,
  } = publicationOptionsPayload

  const publicationOptionPayload = usePublicationOption({ publication_id: publicationId })
  const {
    callbacks: {
      createOrEditPublicationOption,
    },
  } = publicationOptionPayload

  const publicationOptionValuesPayload = usePublicationOptionValues()
  const {
    publicationOptionValues,
    publicationOptionValuesUpdatedAt,
  } = publicationOptionValuesPayload

  const memoColumns = useMemo(
    () => buildColumns(publicationOptions, publicationOptionValues),
    [publicationOptionsUpdatedAt, publicationOptionValuesUpdatedAt],
  )

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-options' }}
      contentBoxProps={{ flexDirection: 'column' }}
      loading={loading}
      title="Options"
    >
      {!loading && hasFilteredPublicationOptions && (
        <Table
          boxProps={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}
          columns={memoColumns}
          data={filteredPublicationOptions}
          scroll={{ x: 800 }}
          stickyHeader
        />
      )}

      {!loading && !hasFilteredPublicationOptions && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Options Present
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please add an option below
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      <DashboardModule.Footer
        boxProps={{
          borderTop: loading ? 'none' : '1px solid',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => createOrEditPublicationOption()}
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Option
        </Button>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

PublicationOptions.propTypes = {
  publicationId: PropTypes.number.isRequired,
}

export default PublicationOptions
